import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListPolicyRules,
  useCreatePolicyRule,
  useUpdatePolicyRule,
  useDeletePolicyRule,
  getListPolicyRulesQueryKey,
  type PolicyRule,
  type PolicyRuleType,
  type PolicyRuleSeverity,
  type CreatePolicyRuleBody,
  type UpdatePolicyRuleBody,
} from "@workspace/api-client-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Shield, Plus, Trash2, Loader2, AlertTriangle, Info, AlertCircle, ChevronDown, ChevronUp, Pencil, Check, X } from "lucide-react";

const RULE_TYPES = [
  { value: "term_cap", label: "Maximum Term Length", description: "Contracts may not exceed N years." },
  { value: "salary_threshold", label: "Salary Threshold", description: "Salaries at or above a threshold require board approval." },
  { value: "required_clause", label: "Required Clause", description: "A clause category or specific clause must be attached." },
  { value: "required_signatory", label: "Required Signatories", description: "High-value contracts must have a minimum number of signatories." },
] as const;

const SEVERITIES = [
  { value: "info", label: "Info", icon: Info, color: "text-blue-600 bg-blue-50 border-blue-200" },
  { value: "warning", label: "Warning", icon: AlertTriangle, color: "text-amber-600 bg-amber-50 border-amber-200" },
  { value: "blocker", label: "Blocker", icon: AlertCircle, color: "text-red-600 bg-red-50 border-red-200" },
] as const;

const CLAUSE_CATEGORIES = [
  "compensation", "termination", "buyout", "nil", "title_ix",
  "indemnification", "governing_law", "outside_income", "general",
];

const CONTRACT_TYPES = [
  { value: "head_coach", label: "Head Coach" },
  { value: "assistant_coach", label: "Assistant Coach" },
  { value: "coordinator", label: "Coordinator" },
  { value: "interim", label: "Interim" },
];

type RuleType = PolicyRuleType;
type Severity = PolicyRuleSeverity;

function SeverityBadge({ severity }: { severity: Severity }) {
  const s = SEVERITIES.find((x) => x.value === severity)!;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${s.color}`}>
      <Icon className="h-3 w-3" />{s.label}
    </span>
  );
}

function RuleParametersForm({
  type,
  value,
  onChange,
}: {
  type: RuleType;
  value: Record<string, unknown>;
  onChange: (v: Record<string, unknown>) => void;
}) {
  if (type === "term_cap") {
    return (
      <div className="space-y-1.5">
        <Label>Maximum term length (years)</Label>
        <Input
          type="number"
          min={1}
          max={20}
          value={(value.maxYears as number) ?? ""}
          onChange={(e) => onChange({ ...value, type: "term_cap", maxYears: parseFloat(e.target.value) || 1 })}
          placeholder="e.g. 5"
        />
      </div>
    );
  }
  if (type === "salary_threshold") {
    return (
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label>Threshold amount ($)</Label>
          <Input
            type="number"
            min={0}
            value={(value.thresholdAmount as number) ?? ""}
            onChange={(e) => onChange({ ...value, type: "salary_threshold", thresholdAmount: parseFloat(e.target.value) || 0 })}
            placeholder="e.g. 1000000"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Approval note (optional)</Label>
          <Input
            value={(value.approvalNote as string) ?? ""}
            onChange={(e) => onChange({ ...value, type: "salary_threshold", approvalNote: e.target.value })}
            placeholder="e.g. Board of Trustees approval required"
          />
        </div>
      </div>
    );
  }
  if (type === "required_clause") {
    return (
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label>Required clause category</Label>
          <Select
            value={(value.clauseCategory as string) ?? ""}
            onValueChange={(v) => onChange({ ...value, type: "required_clause", clauseCategory: v === "__none__" ? undefined : v })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Any category (specific clause IDs below)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Any (use clause IDs instead)</SelectItem>
              {CLAUSE_CATEGORIES.map((c) => (
                <SelectItem key={c} value={c}>{c.replace(/_/g, " ").replace(/\b\w/g, (x) => x.toUpperCase())}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Only apply to contract type (optional)</Label>
          <Select
            value={(value.forContractType as string) ?? ""}
            onValueChange={(v) => onChange({ ...value, type: "required_clause", forContractType: v === "__all__" ? undefined : v })}
          >
            <SelectTrigger>
              <SelectValue placeholder="All contract types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All contract types</SelectItem>
              {CONTRACT_TYPES.map((ct) => (
                <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Only apply to sport (optional)</Label>
          <Input
            value={(value.forSport as string) ?? ""}
            onChange={(e) => onChange({ ...value, type: "required_clause", forSport: e.target.value || undefined })}
            placeholder="e.g. Football"
          />
        </div>
      </div>
    );
  }
  if (type === "required_signatory") {
    return (
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label>Minimum signatories required</Label>
          <Input
            type="number"
            min={1}
            max={10}
            value={(value.minSignatories as number) ?? ""}
            onChange={(e) => onChange({ ...value, type: "required_signatory", minSignatories: parseInt(e.target.value, 10) || 1 })}
            placeholder="e.g. 2"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Only for contracts above ($) (optional)</Label>
          <Input
            type="number"
            min={0}
            value={(value.forContractValueAbove as number) ?? ""}
            onChange={(e) => onChange({ ...value, type: "required_signatory", forContractValueAbove: parseFloat(e.target.value) || undefined })}
            placeholder="e.g. 500000"
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Note: signatories are configured within the approval flow. This rule appears as advisory in the compliance panel and does not block submission.
        </p>
      </div>
    );
  }
  return null;
}

function RuleCard({
  rule,
  onToggle,
  onDelete,
  onSave,
}: {
  rule: PolicyRule;
  onToggle: () => void;
  onDelete: () => void;
  onSave: (updates: UpdatePolicyRuleBody) => Promise<void>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [editName, setEditName] = useState(rule.name);
  const [editSeverity, setEditSeverity] = useState<Severity>(rule.severity);
  const [editMessage, setEditMessage] = useState(rule.message);
  const [editParams, setEditParams] = useState<Record<string, unknown>>(rule.parameters as Record<string, unknown>);

  const typeMeta = RULE_TYPES.find((t) => t.value === rule.type);

  const startEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditName(rule.name);
    setEditSeverity(rule.severity);
    setEditMessage(rule.message);
    setEditParams(rule.parameters as Record<string, unknown>);
    setEditing(true);
    setExpanded(true);
  };

  const cancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditing(false);
  };

  const handleSave = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setSaving(true);
    try {
      const cleanedParams = { ...editParams };
      if (cleanedParams.clauseCategory === "__none__") delete cleanedParams.clauseCategory;
      if (cleanedParams.forContractType === "__all__") delete cleanedParams.forContractType;
      await onSave({
        name: editName.trim(),
        severity: editSeverity,
        message: editMessage.trim(),
        parameters: cleanedParams,
      });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`rounded-lg border overflow-hidden ${rule.isActive ? "border-border" : "border-dashed border-muted-foreground/30 opacity-60"}`}>
      <div
        className="flex items-center gap-3 px-4 py-3 bg-muted/20 cursor-pointer"
        onClick={() => !editing && setExpanded((p) => !p)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm">{rule.name}</span>
            <SeverityBadge severity={rule.severity} />
            {!rule.isActive && <Badge variant="outline" className="text-xs text-muted-foreground">inactive</Badge>}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">{typeMeta?.label}</div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
          >
            {rule.isActive ? "Disable" : "Enable"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            title="Edit rule"
            onClick={startEdit}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
            title="Delete rule"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
          {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
        </div>
      </div>

      {expanded && (
        <div className="px-4 py-3 border-t space-y-3">
          {editing ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Rule name</Label>
                  <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Severity</Label>
                  <Select value={editSeverity} onValueChange={(v) => setEditSeverity(v as Severity)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SEVERITIES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {editSeverity === "blocker" && rule.type === "required_signatory" && (
                    <p className="text-xs text-amber-600">Signatory rules are advisory at submission time regardless of severity.</p>
                  )}
                </div>
                <div className="md:col-span-2 space-y-1.5">
                  <Label>Display message</Label>
                  <Textarea
                    value={editMessage}
                    onChange={(e) => setEditMessage(e.target.value)}
                    className="resize-none h-20"
                  />
                </div>
              </div>
              <Separator />
              <div className="space-y-2">
                <p className="text-sm font-medium">Rule parameters</p>
                <RuleParametersForm type={rule.type} value={editParams} onChange={setEditParams} />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={cancelEdit} disabled={saving}>
                  <X className="h-3.5 w-3.5 mr-1.5" /> Cancel
                </Button>
                <Button size="sm" onClick={handleSave} disabled={saving}>
                  {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1.5" />}
                  Save changes
                </Button>
              </div>
            </>
          ) : (
            <div className="text-sm space-y-2">
              <p className="text-muted-foreground">{rule.message}</p>
              <div className="text-xs font-mono bg-muted/40 p-2 rounded whitespace-pre">
                {JSON.stringify(rule.parameters, null, 2)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function PolicyRulesSettings({ isAdmin }: { isAdmin: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showForm, setShowForm] = useState(false);

  const { data: rules = [], isLoading } = useListPolicyRules();
  const createMutation = useCreatePolicyRule();
  const updateMutation = useUpdatePolicyRule();
  const deleteMutation = useDeletePolicyRule();

  const invalidateRules = () =>
    queryClient.invalidateQueries({ queryKey: getListPolicyRulesQueryKey() });

  const [name, setName] = useState("");
  const [ruleType, setRuleType] = useState<RuleType>("term_cap");
  const [severity, setSeverity] = useState<Severity>("warning");
  const [message, setMessage] = useState("");
  const [parameters, setParameters] = useState<Record<string, unknown>>({ type: "term_cap", maxYears: 5 });

  const resetForm = () => {
    setName("");
    setRuleType("term_cap");
    setSeverity("warning");
    setMessage("");
    setParameters({ type: "term_cap", maxYears: 5 });
    setShowForm(false);
  };

  const handleCreate = async () => {
    if (!name.trim() || !message.trim()) {
      toast({ title: "Missing fields", description: "Name and message are required.", variant: "destructive" });
      return;
    }
    try {
      const cleanedParams = { ...parameters };
      if (cleanedParams.clauseCategory === "__none__") delete cleanedParams.clauseCategory;
      if (cleanedParams.forContractType === "__all__") delete cleanedParams.forContractType;
      const body: CreatePolicyRuleBody = {
        name: name.trim(),
        type: ruleType,
        severity,
        message: message.trim(),
        parameters: cleanedParams,
      };
      await createMutation.mutateAsync({ data: body });
      toast({ title: "Rule created", description: `"${name}" has been added to the policy ruleset.` });
      resetForm();
      await invalidateRules();
    } catch (err) {
      toast({ title: "Create failed", description: err instanceof Error ? err.message : "Error", variant: "destructive" });
    }
  };

  const handleToggle = async (rule: PolicyRule) => {
    try {
      await updateMutation.mutateAsync({ id: rule.id, data: { isActive: !rule.isActive } });
      await invalidateRules();
    } catch (err) {
      toast({ title: "Update failed", description: err instanceof Error ? err.message : "Error", variant: "destructive" });
    }
  };

  const handleSave = async (
    rule: PolicyRule,
    updates: UpdatePolicyRuleBody,
  ) => {
    await updateMutation.mutateAsync({ id: rule.id, data: updates });
    toast({ title: "Rule updated", description: `"${updates.name ?? rule.name}" saved.` });
    await invalidateRules();
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteMutation.mutateAsync({ id });
      await invalidateRules();
      toast({ title: "Rule deleted" });
    } catch (err) {
      toast({ title: "Delete failed", description: err instanceof Error ? err.message : "Error", variant: "destructive" });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" /> Policy Rules
            </CardTitle>
            <CardDescription className="mt-1">
              Define org-level compliance rules. Contracts with failing blocker rules cannot be submitted for approval.
            </CardDescription>
          </div>
          {isAdmin && !showForm && (
            <Button size="sm" onClick={() => setShowForm(true)}>
              <Plus className="h-4 w-4 mr-2" /> Add rule
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isAdmin && showForm && (
          <>
            <div className="rounded-lg border border-dashed p-4 space-y-4 bg-muted/20">
              <p className="text-sm font-medium">New Policy Rule</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Rule name *</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Maximum 5-year term" />
                </div>
                <div className="space-y-1.5">
                  <Label>Rule type *</Label>
                  <Select
                    value={ruleType}
                    onValueChange={(v) => {
                      const t = v as RuleType;
                      setRuleType(t);
                      if (t === "term_cap") setParameters({ type: "term_cap", maxYears: 5 });
                      else if (t === "salary_threshold") setParameters({ type: "salary_threshold", thresholdAmount: 1000000 });
                      else if (t === "required_clause") setParameters({ type: "required_clause" });
                      else if (t === "required_signatory") setParameters({ type: "required_signatory", minSignatories: 2 });
                    }}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {RULE_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Severity *</Label>
                  <Select value={severity} onValueChange={(v) => setSeverity(v as Severity)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SEVERITIES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Blockers prevent approval submission. Warnings are advisory. Info is informational only.</p>
                </div>
                <div className="space-y-1.5">
                  <Label>Display message *</Label>
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="e.g. Contract term exceeds the maximum allowed 5 years. Board approval required."
                    className="resize-none h-20"
                  />
                </div>
              </div>
              <Separator />
              <div className="space-y-2">
                <p className="text-sm font-medium">Rule parameters</p>
                <RuleParametersForm type={ruleType} value={parameters} onChange={setParameters} />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={resetForm}>Cancel</Button>
                <Button onClick={handleCreate} disabled={createMutation.isPending}>
                  {createMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                  Create rule
                </Button>
              </div>
            </div>
            <Separator />
          </>
        )}

        {isLoading ? (
          <div className="text-sm text-muted-foreground flex items-center gap-2 py-4">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading rules…
          </div>
        ) : rules.length === 0 ? (
          <div className="text-center py-8 space-y-2">
            <Shield className="h-8 w-8 text-muted-foreground mx-auto" />
            <p className="text-sm text-muted-foreground">No policy rules defined yet.</p>
            {isAdmin && !showForm && (
              <Button variant="outline" size="sm" onClick={() => setShowForm(true)}>
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Add your first rule
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {rules.map((rule) => (
              <RuleCard
                key={rule.id}
                rule={rule}
                onToggle={() => handleToggle(rule)}
                onDelete={() => handleDelete(rule.id)}
                onSave={(updates) => handleSave(rule, updates)}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
